import React from 'react';
import ReactDOM from 'react-dom/client';
import { Provider as ReduxProvider } from 'react-redux';
import { RouterProvider } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { CssBaseline } from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { fr as frLocale, enUS, es, pt } from 'date-fns/locale';
import type { Locale } from 'date-fns';
import { MotionConfig } from 'framer-motion';
import {
  lightPalette,
  darkPalette,
  typography,
  components,
} from 'theme';

import { store } from './app/store';
import { router } from './app/router';
import i18n from './app/i18n';

import '@fontsource/plus-jakarta-sans/400.css';
import '@fontsource/plus-jakarta-sans/500.css';
import '@fontsource/plus-jakarta-sans/700.css';
import '@fontsource/plus-jakarta-sans/800.css';
import '@fontsource/inter/400.css';
import '@fontsource/inter/500.css';
import '@fontsource/inter/600.css';
import '@fontsource/inter/700.css';

// ---------------------------------------------------------------------------
// date-fns locale resolution for <LocalizationProvider>
// ---------------------------------------------------------------------------

/** Map of supported language codes to their date-fns Locale objects. */
const DATE_FNS_LOCALE_MAP: Record<string, Locale> = {
  fr: frLocale,
  en: enUS,
  es: es,
  pt: pt,
};

/**
 * Pick the appropriate date-fns Locale from the current i18n language.
 * Called once at module load. The DateTimePicker locale won't react to
 * language changes at runtime without a full remount — acceptable trade-off
 * for this polish task. A reactive solution would require lifting
 * LocalizationProvider into a stateful component and listening to
 * `i18n.on('languageChanged', ...)`.
 */
function pickDateFnsLocale(): Locale {
  const lng = (i18n.language ?? 'fr').split('-')[0]!;
  return DATE_FNS_LOCALE_MAP[lng] ?? frLocale;
}

const dateFnsLocale = pickDateFnsLocale();

// v7-style theme: cssVariables + colorSchemes — forward-compatible with MUI v7.
const theme = createTheme({
  cssVariables: {
    colorSchemeSelector: 'data-mui-color-scheme',
  },
  colorSchemes: {
    // The package palettes are MUI v6/v7-compatible PaletteOptions.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    light: { palette: lightPalette as any },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    dark: { palette: darkPalette as any },
  },
  typography,
  // `components` is typed against the theme package's nested @mui/material
  // install. yarn workspaces with `file:..` deps create a duplicate MUI install
  // in node_modules/theme/node_modules/@mui, so the two Theme types are not
  // structurally identical. Cast at the boundary — the runtime shape is fine.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  components: components as any,
  shape: { borderRadius: 12 },
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ReduxProvider store={store}>
      <HelmetProvider>
        <ThemeProvider theme={theme} defaultMode="system">
          <CssBaseline />
          {/* Honor OS-level `prefers-reduced-motion`: when the user has reduced
              motion on, framer-motion zeroes-out transition durations app-wide. */}
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={dateFnsLocale}>
            <MotionConfig reducedMotion="user">
              <RouterProvider router={router} />
            </MotionConfig>
          </LocalizationProvider>
        </ThemeProvider>
      </HelmetProvider>
    </ReduxProvider>
  </React.StrictMode>,
);
